// Zustand und Zeitmessung des Benachrichtigungs-Balkens.
//
// ⚠️ Warum das ein eigener Haken ist und nicht in `Benachrichtigung.tsx` steht: von den zwölf
// gemessenen Handkopien schrieben ELF ihren `setTimeout` selbst. Ein Baustein ohne Haken hätte nur
// die Fläche eingesammelt — die nächste Seite hätte den Timer wieder abgeschrieben, und damit auch
// die Fehler darin. Gemessen:
//
// - *Alben / Interpreten / Playlists*: `setTimeout` im `notify`-Aufruf, OHNE `clearTimeout`. Der
//   Timer überlebt das Ausbauen der Seite und schreibt danach in einen toten Zustand; bei zwei
//   Meldungen kurz nacheinander räumt der ältere Timer die jüngere Meldung weg.
// - *Audio / Video / MindBooks / Themen-Kategorien / Mediathek*: Effekt mit sauberem Cleanup, aber
//   für BEIDE Tonlagen — Fehler verschwanden also ebenfalls.
// - *Benutzer*: Effekt mit Cleanup und der Bedingung `type !== 'success'` davor. Das ist die
//   Fassung, die hier steht.
//
// Die Rückgabe heißt bewusst `melde(type, message)` und nicht `setInhalt(objekt)`: `AlbumForm`,
// `ArtistForm` und `PlaylistForm` erwarten eine Eigenschaft
// `notify: (type: BenachrichtigungsTyp, message: string) => void`. Mit dieser Form lässt sich der
// Haken dort ohne Anfassen der Aufrufe einsetzen. (Die drei Formulare schrieben die Tonlagen
// anfangs als `'success' | 'error'` ab — eine Zweitquelle neben `BenachrichtigungsTyp`; sie
// importieren den geteilten Typ inzwischen.)
import { useCallback, useEffect, useState } from 'react';
import type { BenachrichtigungsInhalt, BenachrichtigungsTyp } from '../components/ui/Benachrichtigung';

/**
 * Anzeigedauer einer Erfolgsmeldung.
 *
 * ⚠️ Nur Erfolg. Ein Fehler bleibt stehen, bis die Nutzerin handelt — neu versuchen, filtern oder
 * auf „Schließen" gehen. Begründung ausführlich in `Benachrichtigung.tsx`.
 *
 * 3000 ms ist die Mehrheitsfassung (sechs der zwölf Kopien); Alben, Interpreten und Playlists
 * fuhren 4000 ms und verlieren beim Angleichen eine Sekunde.
 */
export const ERFOLG_ANZEIGEDAUER_MS = 3000;

export interface BenachrichtigungsSteuerung {
  /** Was gerade zu sehen ist. Direkt an `<Benachrichtigung inhalt={…} />` weiterreichen. */
  inhalt: BenachrichtigungsInhalt | null;
  /** Meldung setzen. Signatur passend zur `notify`-Eigenschaft der bestehenden Formulare. */
  melde: (type: BenachrichtigungsTyp, message: string) => void;
  /** Meldung wegräumen. Hängt am Schließen-Knopf des Balkens. */
  schließe: () => void;
}

export const useBenachrichtigung = (): BenachrichtigungsSteuerung => {
  const [inhalt, setInhalt] = useState<BenachrichtigungsInhalt | null>(null);

  // `useCallback`, damit die Funktionen ihre Identität behalten. Das ist keine Optimierung, sondern
  // Pflicht: die Ladefunktionen der Seiten sind `useCallback([… , notify])` und hängen an einem
  // Effekt. Eine bei jedem Rendern neue `melde`-Funktion würde diese Kette bei jeder Meldung neu
  // auslösen — die Seite lüde nach jedem Speichern doppelt.
  const melde = useCallback((type: BenachrichtigungsTyp, message: string) => {
    setInhalt({ type, message });
  }, []);

  const schließe = useCallback(() => setInhalt(null), []);

  useEffect(() => {
    if (inhalt?.type !== 'success') return;
    const timer = setTimeout(() => setInhalt(null), ERFOLG_ANZEIGEDAUER_MS);
    // Das `clearTimeout` fehlte in drei der Kopien. Ohne es läuft der Timer nach dem Ausbauen der
    // Seite weiter, und zwei kurz aufeinanderfolgende Meldungen teilen sich einen Timer: der ältere
    // räumt die jüngere weg, statt dass jede ihre vollen drei Sekunden bekommt.
    return () => clearTimeout(timer);
  }, [inhalt]);

  return { inhalt, melde, schließe };
};
